import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { map, assoc, concat, addIndex } from 'ramda';
import { SortableContainer, SortableElement, arrayMove } from 'react-sortable-hoc';
import { v4 } from 'uuid';
import { UploadImage } from '@wayfinder/vms-base';
import UploadedImage from '../uploaded-image/UploadedImage';
import ImageDropzone from '../image-dropzone/ImageDropzone';
import Spinner from '../spinner/Spinner';
import { FILE_SIZE_LIMITS, ACCEPTED_IMAGE_TYPES, FILE_SIZE_ERROR_MESSAGE } from '../../constants/file-upload.js';
import s from './ImageUploader.module.scss';

export default class ImageUploader extends Component {
    static propTypes = {
        imageList: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                type: PropTypes.string,
                caption: PropTypes.string,
                dateUploaded: PropTypes.object,
                uri: PropTypes.string,
                dimensions: PropTypes.object,
            }),
        ),
        accountId: PropTypes.string,
        containsFailed: PropTypes.func,
        disabled: PropTypes.bool,
        onItemAdditions: PropTypes.func,
        onItemRemoval: PropTypes.func,
        onListOrderChange: PropTypes.func,
        onPropChange: PropTypes.func,
        type: PropTypes.string,
        openImageInNewTab: PropTypes.func,
    };

    static defaultProps = {
        accountId: '',
        containsFailed: () => {},
        disabled: false,
        imageList: [],
        onItemAdditions: () => {},
        onItemRemoval: () => {},
        onListOrderChange: () => {},
        onPropChange: () => {},
        type: '',
        openImageInNewTab: () => {},
    };

    constructor(props) {
        super(props);
        this.state = { rejectedFiles: [] };
    }

    handleRemoveImg = img => {
        const { type, onItemRemoval } = this.props;
        onItemRemoval(type, img);
    };

    onUpdateImgAltText = (img, newAltText) => {
        const { onPropChange, type } = this.props;

        onPropChange(type, img, 'alternativeText', newAltText);
    };

    handleRemoveRejectedFile = file => {
        const { rejectedFiles } = this.state;
        const { containsFailed, type } = this.props;

        const newRejectedFiles = rejectedFiles.filter(f => f.id !== file.id);
        this.setState({ rejectedFiles: newRejectedFiles });
        containsFailed(type, newRejectedFiles.length !== 0);
    };

    onSortEnd = ({ oldIndex, newIndex }) => {
        const { imageList, onListOrderChange, type } = this.props;

        onListOrderChange(type, arrayMove(imageList, oldIndex, newIndex));
    };

    readImage = file => {
        const { onItemAdditions, type } = this.props;
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            const image = new Image();
            image.addEventListener('load', () => {
                const uploadFile = UploadImage.convertToImage(file, image);
                onItemAdditions(type, [uploadFile]);
            });
            image.src = URL.createObjectURL(file);
        });

        reader.readAsDataURL(file);
    };

    onFileDrop = ({ accepted, rejected }) => {
        const { rejectedFiles } = this.state;
        const { containsFailed, type } = this.props;

        map(acceptedImage => this.readImage(acceptedImage), accepted);

        const newlyRejected = map(assoc('id', v4()), rejected);
        const newRejectedFiles = concat(rejectedFiles, newlyRejected);

        this.setState({ rejectedFiles: newRejectedFiles });

        containsFailed(type, newRejectedFiles.length !== 0);
    };

    renderImg = img => (
        <div className={s.uploadedImgUnit} data-testid={"uploaded-image"}>
            <button className={s.delete} onClick={() => this.handleRemoveImg(img)} type="button" data-testid={"delete-image-button"}>
                <div className={classNames(s.deleteImg, 'icon-trash')} />
            </button>
            <UploadedImage image={img} accountId={this.props.accountId} editAltText={this.onUpdateImgAltText} openImageInNewTab={this.props.openImageInNewTab} />
        </div>
    );

    renderRejectedFile = file => (
        <div className={classNames(s.imgChildContainer, s.rejectedFileContainer)}>
            <div className={classNames(s.rejectImg, s.rejectedSkeleton)}>
                <button className={classNames(s.rejectDelete, 'icon-closecircle')} onClick={() => this.handleRemoveRejectedFile(file)} type="button" />
                <div className={s.failed}>FAILED</div>
                <div className={s.failedName}>{file.name}</div>
                <div className={s.failedDescriptionContainer}>
                    <div className={s.failedDescription}>{FILE_SIZE_ERROR_MESSAGE}</div>
                </div>
            </div>
        </div>
    );

    renderImageDropzone = () => {
        const { type, disabled } = this.props;

        const single = type === 'logo';
        const acceptedFileTypes = ACCEPTED_IMAGE_TYPES;
        const maxFileSize = FILE_SIZE_LIMITS.LOGO;
        return <ImageDropzone onFileDrop={this.onFileDrop} multiple={!single} acceptedFileTypes={acceptedFileTypes} maxFileSize={maxFileSize} disabled={disabled} />;
    };

    checkImageListType = () => {
        const { rejectedFiles } = this.state;
        const { imageList, type } = this.props;

        const single = type === 'logo';

        if (!(single && imageList.length + rejectedFiles.length >= 1)) {
            return <div>{this.renderImageDropzone()}</div>;
        }
    };

    renderLoadingItem = () => (
        <div className={classNames(s.imgChildContainer, s.loadingFileContainer)}>
            <div className={classNames(s.loadingImg, s.loadingSkeleton)}>
                <Spinner loading />
            </div>
            <div className={classNames(s.loadingCaption, s.loadingSkeleton)} />
            <div className={classNames(s.loadingDateUploaded, s.loadingSkeleton)} />
        </div>
    );

    render = () => {
        const { imageList, disabled } = this.props;
        const { rejectedFiles } = this.state;
        const mapIndexed = addIndex(map);
        const SortableItem = SortableElement(({ value }) => this.renderImg(value));
        const RejectedItem = SortableElement(({ value }) => this.renderRejectedFile(value));

        const SortableList = SortableContainer(({ items }) => (
            <div className={s.filesContainer}>
                {mapIndexed(
                    (value, index) => (
                        <SortableItem key={`${value.name}-${Math.random()}`} index={index} value={value} disabled={disabled} />
                    ),
                    items,
                )}
                {mapIndexed(
                    (value, index) => (
                        <RejectedItem key={`${value.name}-${Math.random()}`} index={index} value={value} disabled />
                    ),
                    rejectedFiles,
                )}
                {this.checkImageListType()}
            </div>
        ));

        return <SortableList items={imageList} axis="xy" onSortEnd={this.onSortEnd} />;
    };
}
